import { useState, useCallback } from 'react';
import { openFoodFactsService } from '../services';
import { generateFoodId, showNotification } from '../utils/helpers';

/**
 * Custom hook for Open Food Facts integration
 * @param {Array} existingFoods - Current foods list for ID generation
 * @param {Function} onFoodAdded - Callback when food is added
 * @returns {Object} Open Food Facts state and methods
 */
export const useOpenFoodFacts = (existingFoods = [], onFoodAdded) => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [error, setError] = useState(null);
  const [scannedProduct, setScannedProduct] = useState(null);

  /**
   * Search product by barcode - stocke le résultat pour prévisualisation
   * @param {string} barcode - Product barcode
   */
  const searchByBarcode = useCallback(async (barcode) => {
    if (!barcode) {
      setError('Code-barres requis');
      return;
    }

    setIsSearching(true);
    setError(null);
    setScannedProduct(null);

    try {
      const product = await openFoodFactsService.searchByBarcode(barcode);

      if (product) {
        // Générer un ID et stocker pour prévisualisation (pas d'ajout auto)
        product.id = generateFoodId(existingFoods);
        setScannedProduct(product);
        showNotification(`Produit trouvé : ${product.name}`, 'info');
      } else {
        setError('Produit non trouvé dans Open Food Facts');
        showNotification('Produit non trouvé dans Open Food Facts', 'error');
      }
    } catch (error) {
      console.error('Error searching by barcode:', error);
      setError(error.message);
      showNotification(error.message, 'error');
    } finally {
      setIsSearching(false);
    }
  }, [existingFoods]);

  /**
   * Ajouter le produit scanné après validation par l'utilisateur
   */
  const addScannedProduct = useCallback(() => {
    if (!scannedProduct) return;

    try {
      if (onFoodAdded) {
        onFoodAdded(scannedProduct);
      }
      showNotification(`Produit ajouté : ${scannedProduct.name}`, 'success');
      setScannedProduct(null);
    } catch (error) {
      console.error('Error adding scanned product:', error);
      showNotification('Erreur lors de l\'ajout du produit', 'error');
    }
  }, [scannedProduct, onFoodAdded]);

  /**
   * Annuler la prévisualisation du produit scanné
   */
  const clearScannedProduct = useCallback(() => {
    setScannedProduct(null);
  }, []);

  /**
   * Search products by name
   * @param {string} productName - Product name to search
   */
  const searchByName = useCallback(async (productName) => {
    if (!productName) {
      setError('Nom de produit requis');
      return;
    }

    setIsSearching(true);
    setError(null);
    setShowSearchResults(false);

    try {
      const results = await openFoodFactsService.searchByName(productName);
      
      if (results.length > 0) {
        setSearchResults(results);
        setShowSearchResults(true);
      } else {
        setError('Aucun produit trouvé avec ce nom');
        showNotification('Aucun produit trouvé avec ce nom', 'info');
      }
    } catch (error) {
      console.error('Error searching by name:', error);
      setError(error.message);
      showNotification(error.message, 'error');
    } finally {
      setIsSearching(false);
    }
  }, []);

  /**
   * Add selected product from search results
   * @param {Object} selectedProduct - Product to add
   */
  const addSelectedProduct = useCallback((selectedProduct) => {
    if (!selectedProduct) return;

    try {
      // Generate proper ID for the product
      const productToAdd = {
        ...selectedProduct,
        id: generateFoodId(existingFoods)
      };

      if (onFoodAdded) {
        onFoodAdded(productToAdd);
      }

      // Clear search results
      setSearchResults([]);
      setShowSearchResults(false);
      setError(null);
      
      showNotification(`Produit ajouté : ${productToAdd.name}`, 'success');
    } catch (error) {
      console.error('Error adding selected product:', error);
      setError('Erreur lors de l\'ajout du produit');
      showNotification('Erreur lors de l\'ajout du produit', 'error');
    }
  }, [existingFoods, onFoodAdded]);

  /**
   * Clear search results
   */
  const clearSearchResults = useCallback(() => {
    setSearchResults([]);
    setShowSearchResults(false);
    setError(null);
  }, []);

  /**
   * Clear all state
   */
  const clearAll = useCallback(() => {
    setSearchResults([]);
    setShowSearchResults(false);
    setError(null);
    setIsSearching(false);
    setScannedProduct(null);
  }, []);

  /**
   * Get product quality assessment
   * @param {Object} product - Product to assess
   * @returns {Object} Quality assessment
   */
  const getProductQuality = useCallback((product) => {
    const score = openFoodFactsService.getProductQualityScore(product);
    const isComplete = openFoodFactsService.isProductDataComplete(product);
    
    return {
      score,
      isComplete,
      rating: score >= 8 ? 'excellent' : score >= 6 ? 'good' : score >= 4 ? 'fair' : 'poor'
    };
  }, []);

  return {
    // State
    isSearching,
    searchResults,
    showSearchResults,
    error,
    scannedProduct,

    // Methods
    searchByBarcode,
    searchByName,
    addSelectedProduct,
    addScannedProduct,
    clearScannedProduct,
    clearSearchResults,
    clearAll,
    getProductQuality,

    // Computed
    hasResults: searchResults.length > 0,
    canSearch: !isSearching
  };
};